<!-- eslint-disable-next-line vue/max-lines-per-block -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { nextTick, ref, watch } from 'vue'
import { array, object, string, enum as stringUnion } from 'zod'

import type { DocIdentifier } from '@/types/external/2work/api/common/DocIdentifier'
import type { BRMPerson } from '@/types/external/2work/doctypes/CustomDoctypes'
import type { Contact } from '@/types/external/2work/doctypes/generatorOutput/Contacts/Contact'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import ButtonPrimary from '@/components/common/ui/buttons/ButtonPrimary.vue'
import ButtonSecondary from '@/components/common/ui/buttons/ButtonSecondary.vue'
import AlertMessage from '@/components/common/ui/overlays/AlertMessage.vue'
import CreatePersonFormLong from '@/components/createRelationGroup/CreatePersonFormLong.vue'
import CreatePersonFormShort from '@/components/createRelationGroup/CreatePersonFormShort.vue'
import { useZodSchemaStore } from '@/components/editRelationGroup/sectionSelfUpdates/zodSchemaStore'
import { useNetworkError } from '@/composables/useNetworkError'
import { useScrollToFirstError } from '@/composables/useScrollToFirstError'
import { AlertType } from '@/constants/ui'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import {
  translationFunctionBackend,
  translationFunctionCommon,
  translationFunctionDefault,
} from '@/utilities/translations'

interface CreatePersonFormProps {
  onCreated(response: DocIdentifier): void
}

const props = defineProps<CreatePersonFormProps>()
const emit = defineEmits<{ dirtyChange: [boolean] }>()
const formRef = ref<HTMLElement | null>(null)
const scrollToFirstError = useScrollToFirstError(formRef)

const tfBackend = translationFunctionBackend()
const tfCommon = translationFunctionCommon()
const tfDefault = translationFunctionDefault()

const creationLoading = ref(false)

const simpleNotificationStore = useSimpleNotificationStore()

const createRelationSlideInStore = useCreateRelationSlideInStore()
const { closeCreateRelationOverlay } = createRelationSlideInStore

const createPersonWithNoOrgModalOpen = ref(false)
const setCreatePersonWithNoOrgModalOpen = (value: boolean): void => {
  createPersonWithNoOrgModalOpen.value = value
}

const prefillNameParts = (createRelationSlideInStore.prefillMetaData?.prefillName ?? '')
  .trim()
  .split(/\s+/u)
  .filter(Boolean)
const initialValues = {
  email_ids: [],
  first_name:
    prefillNameParts.length > 1
      ? prefillNameParts.slice(0, -1).join(' ')
      : (prefillNameParts[0] ?? ''),
  last_name: prefillNameParts.length > 1 ? (prefillNameParts.at(-1) ?? '') : '',
  links: [],
  phone_nos: [],
  salutation: null,
  tw_addresses: [],
  tw_contact_origin: '',
  tw_date_of_birth: undefined,
  tw_description: '',
  tw_primary_address: undefined,
  tw_rating: createRelationSlideInStore.prefillMetaData?.tw_rating ?? '',
  tw_responsible_employee:
    createRelationSlideInStore.prefillMetaData?.tw_responsible_employee ?? null,
  tw_user_tags: [],
  tw_weblinks: [],
}

const customAPIStore = useFrappeNestCustomAPIStore()

const {
  commonSchemas: { dynamicLinkListSchema },
  relationSchemas: {
    addressListSchema,
    emailSchema,
    personSchemas: { generalDataPersonSchema, metaDataPersonSchema },
    phoneNumberSchema,
    primaryAddressSchema,
    refineMents: { addressRefinement },
    weblinkSchema,
  },
} = useZodSchemaStore()

const validationSchema = object({
  doctype: stringUnion(['Contact']).catch('Contact'),
  tw_user_tags: array(string()).optional(),
})
  .extend(emailSchema.shape)
  .extend(generalDataPersonSchema.shape)
  .extend(metaDataPersonSchema.shape)
  .extend(weblinkSchema.shape)
  .extend(phoneNumberSchema.shape)
  .extend(addressListSchema.shape)
  .extend(primaryAddressSchema.shape)
  .extend(dynamicLinkListSchema.shape)
  .superRefine(addressRefinement)

const { handleSubmit, meta, resetForm } = useForm({
  initialValues,
  validateOnMount: false,
  validationSchema: toTypedSchema(validationSchema),
})

watch(
  meta,
  // eslint-disable-next-line id-length
  (m) => {
    emit('dirtyChange', m.dirty)
  },
  { immediate: true },
)

const onError = (): void => {
  resetForm()
  simpleNotificationStore.triggerSimpleNotificationError(
    tfDefault('editRelationGroup.createError', {
      createObjectTranslation: tfBackend('relationGroupType.persons'),
    }),
  )
}

const handleNetworkError = useNetworkError(onError)

const onSubmit = handleSubmit(
  async (values) => {
    creationLoading.value = true
    if (!createPersonWithNoOrgModalOpen.value && values.links.length === 0) {
      setCreatePersonWithNoOrgModalOpen(true)

      return
    }
    const payload = {
      ...values,
      tw_addresses: values.tw_addresses?.length === 0 ? undefined : values.tw_addresses,
      tw_responsible_employee: { doctype: 'Employee', name: values.tw_responsible_employee },
    }

    await customAPIStore
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      .createPerson(payload as Partial<BRMPerson>)
      .then(async (response: BRMPerson) => {
        resetForm()
        // if associated orgs do not have any contacts yet, set the new person as primary contact
        const orgsWithoutPrimaryContect = response.organizations
          .filter((org) => org.tw_primary_contact === null)
          .map((org) => {
            return {
              doctype: org.doctype,
              name: org.name,
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              tw_primary_contact: { doctype: 'Contact', name: response.name } as Contact,
            }
          })
        if (orgsWithoutPrimaryContect.length > 0) {
          await customAPIStore
            .updateOrganizationList(orgsWithoutPrimaryContect)
            .catch(async (networkError: unknown) => {
              console.error('Error updating organizations:', networkError)
              await handleNetworkError(networkError)
            })
        }

        const { doctype, name }: DocIdentifier = response
        props.onCreated({ doctype, name })

        simpleNotificationStore.triggerSimpleNotificationSuccess(
          tfDefault('editRelationGroup.createSuccess', {
            createObjectTranslation: tfBackend('relationGroupType.persons'),
          }),
        )
      })
      .catch(async (networkError: unknown) => {
        // console.error('Error creating person:', networkError)
        await handleNetworkError(networkError)
      })
      .finally(() => {
        creationLoading.value = false
      })
  },
  (context) => {
    scrollToFirstError(context)
    if (Object.keys(context.errors).length > 0) {
      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('editRelationGroup.createErrorValidation', {
          validationObjectTranslation: tfBackend('relationGroupType.persons'),
        }),
      )
    }
  },
)

const onReset = (): void => {
  resetForm()
  closeCreateRelationOverlay()
}

const showAllFields = ref(false)
const setShowAllFields = (): void => {
  showAllFields.value = true
}

const closeWarningModal = (): void => {
  creationLoading.value = false
  setCreatePersonWithNoOrgModalOpen(false)
}

const acceptModal = async (): Promise<void> => {
  await onSubmit()
  closeWarningModal()
  await nextTick()
  closeCreateRelationOverlay()
}
</script>

<template>
  <div class="flex h-full flex-1 flex-col justify-between overflow-hidden">
    <div
      ref="formRef"
      class="h-full flex-1 overflow-auto"
      :data-testid="TEST_IDS_CREATE.createPersonForm.root"
    >
      <CreatePersonFormShort v-if="!showAllFields" :on-show-all-fields="setShowAllFields" />
      <CreatePersonFormLong v-else />
    </div>

    <div class="flex gap-8 p-8">
      <ButtonPrimary
        class="flex-1"
        :data-testid="TEST_IDS_CREATE.createPersonForm.submit"
        :label="tfCommon('button.create')"
        :loading="creationLoading"
        @click="onSubmit"
      />
      <ButtonSecondary
        class="flex-1"
        :data-testid="TEST_IDS_CREATE.createPersonForm.cancel"
        :label="tfCommon('button.cancel')"
        @click="onReset"
      />
    </div>
  </div>
  <AlertMessage
    :alert-type="AlertType.warning"
    :cancel-label="tfDefault('createRelation.alertNoLinkedOrgCancelLabel')"
    :cta-label="tfDefault('createRelation.alertNoLinkedOrgCtaLabel')"
    :data-testid="TEST_IDS_CREATE.createPersonForm.alertNoLinkedOrg"
    :message="tfDefault('createRelation.alertNoLinkedOrgMsg')"
    :message-title="tfDefault('createRelation.alertNoLinkedOrgTitle')"
    :open="createPersonWithNoOrgModalOpen"
    @on-close="acceptModal"
    @on-cta-click="closeWarningModal"
  />
</template>
